import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CheckCircleOutlined, InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { ordersService } from '../../services';
import { ProductCategory } from '../../constants/productCategory';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../contexts/ProductsContext';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { useMarketplacePricing } from '../../hooks/useMarketplacePricing';
import MarketplacePricingSelector from '../../components/MarketplacePricingSelector';

// ─── Types ───────────────────────────────────────────────
interface MetabaseConfig {
  name: string;
  description: string;
  region: string;
  adminEmail: string;
}

// ─── Component ───────────────────────────────────────────
const CreateMetabase: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { products: allProducts, getProductsByCategory } = useProducts();
  const { success, error: showError } = useNotificationContext();

  const [step, setStep] = useState(1);
  const pricing = useMarketplacePricing();
  const [isCreating, setIsCreating] = useState(false);

  const [config, setConfig] = useState<MetabaseConfig>({
    name: '',
    description: '',
    region: 'us-east-1',
    adminEmail: '',
  });

  const products = allProducts[ProductCategory.METABASE] || [];

  useEffect(() => {
    if (accessToken && products.length === 0) {
      getProductsByCategory(ProductCategory.METABASE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // ─── Validations ───────────────────────────────────────
  const isStepValid = (): boolean => {
    switch (step) {
      case 1: return !!(config.name.trim() && pricing.isReady);
      case 2: return config.adminEmail.includes('@') && config.adminEmail.includes('.');
      case 3: return true;
      default: return false;
    }
  };

  // ─── Handlers ──────────────────────────────────────────
  const handleNext = () => {
    if (step < 3 && isStepValid()) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCreate = async () => {
    if (!config.name || !pricing.pricingSelection) {
      showError(t('metabase.validation.missingFields') || 'Please complete all required fields.');
      return;
    }

    setIsCreating(true);
    try {
      const selectedProduct = products.find((p: any) => p.category === 'METABASE') || products[0];

      if (!selectedProduct || !selectedProduct.id) {
        showError(t('metabase.validation.noProduct') || 'No product available. Please try again.');
        setIsCreating(false);
        return;
      }

      const instanceType = pricing.selectedVariant?.providerConfiguration?.instanceType ?? 't3.medium';
      const storage = pricing.selectedVariant?.includedResources?.storageGb ?? 30;

      const orderData: any = {
        productId: selectedProduct.id,
        quantity: 1,
        metadata: {
          type: 'METABASE',
          metabaseConfig: {
            name: config.name.trim(),
            description: config.description.trim(),
            pricingSelection: pricing.pricingSelection,
            instanceType,
            storage,
            projectName: config.name.trim(),
            region: config.region,
            adminEmail: config.adminEmail,
          },
        },
      };

      const createdOrder = await ordersService.create(orderData);
      success(t('metabase.create.success') || 'Metabase order created successfully');
      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error: any) {
      console.error('Error creating Metabase order:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error creating order.';
      showError(errorMessage);
      setIsCreating(false);
    }
  };

  // ─── Step 1: Infrastructure ────────────────────────────
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          {t('metabase.step1.title') || 'Infrastructure Configuration'}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('metabase.step1.subtitle') || 'Configure the server that will host your Metabase BI instance.'}
        </p>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('metabase.fields.name') || 'Service Name'} *
        </label>
        <input
          type="text"
          value={config.name}
          onChange={(e) => setConfig({ ...config, name: e.target.value })}
          placeholder="my-metabase"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('metabase.fields.nameHint') || 'A unique name to identify your Metabase BI service.'}
        </p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('metabase.fields.description') || 'Description'} <span className="text-gray-400">(optional)</span>
        </label>
        <textarea
          value={config.description}
          onChange={(e) => setConfig({ ...config, description: e.target.value })}
          placeholder={t('metabase.fields.descriptionPlaceholder') || 'Optional description...'}
          rows={2}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
        />
      </div>

      {/* Pricing plans */}
      <MarketplacePricingSelector
        basePlans={pricing.basePlans}
        variantsForSelected={pricing.variantsForSelected}
        selectedPlanCode={pricing.selectedPlanCode}
        selectedVariantCode={pricing.selectedVariantCode}
        addons={pricing.addons}
        selectedAddonCodes={pricing.selectedAddonCodes}
        isLoadingPlans={pricing.isLoadingPlans}
        isLoadingAddons={pricing.isLoadingAddons}
        onSelectPlan={pricing.setSelectedPlanCode}
        onSelectVariant={pricing.setSelectedVariantCode}
        onToggleAddon={pricing.toggleAddon}
      />

      {/* Region (fixed) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('metabase.fields.region') || 'Region'}
        </label>
        <div className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300">
          US East (N. Virginia)
        </div>
      </div>
    </div>
  );

  // ─── Step 2: Metabase Configuration ──────────────────────
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          {t('metabase.step2.title') || 'Metabase Configuration'}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('metabase.step2.subtitle') || 'Configure settings for your Metabase BI instance.'}
        </p>
      </div>

      {/* Admin Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('metabase.fields.adminEmail') || 'Admin Email'} *
        </label>
        <input
          type="email"
          value={config.adminEmail}
          onChange={(e) => setConfig({ ...config, adminEmail: e.target.value })}
          placeholder="admin@example.com"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('metabase.fields.adminEmailHint') || 'Email address for the admin account that will be created automatically.'}
        </p>
      </div>

      {/* Admin Password Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {t('metabase.step2.adminPassword') || 'Admin password'}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              {t('metabase.step2.adminPasswordDesc') || 'A secure password will be auto-generated and stored in Secrets Manager. You can view it in the service detail after deployment.'}
            </p>
          </div>
        </div>
      </div>

      {/* Database info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {t('metabase.step2.database') || 'Application Database'}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              {t('metabase.step2.databaseDesc') || 'PostgreSQL is used as the application database for Metabase. DB credentials are stored in Secrets Manager.'}
            </p>
          </div>
        </div>
      </div>

      {/* Ports info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {t('metabase.step2.accessInfo') || 'Access'}
            </p>
            <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-0.5 list-disc list-inside">
              <li>HTTPS via ALB (Application Load Balancer)</li>
              <li>3000 — Metabase (internal)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Step 3: Review ────────────────────────────────────
  const renderStep3 = () => {
    const variant = pricing.selectedVariant;
    const inst = variant?.providerConfiguration?.instanceType ?? '—';
    const storageGb = variant?.includedResources?.storageGb;
    const totalStr = pricing.totalPrice
      ? `$${pricing.totalPrice.toLocaleString('es-MX', { minimumFractionDigits: 0 })} MXN`
      : '—';

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            {t('metabase.step3.title') || 'Review & Create'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('metabase.step3.subtitle') || 'Review your Metabase BI configuration before creating the service.'}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">
            {t('metabase.review.infrastructure') || 'Infrastructure'}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('metabase.fields.name') || 'Name'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{config.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Plan</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {variant?.displayName ?? variant?.planName ?? '—'} ({variant?.variantName})
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('metabase.fields.instanceType') || 'Instance'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{inst}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('metabase.fields.storage') || 'Storage'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{storageGb ? `${storageGb} GB (gp3)` : '—'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('metabase.fields.region') || 'Region'}</span>
              <span className="font-medium text-gray-900 dark:text-white">US East (N. Virginia)</span>
            </div>
            {config.description && (
              <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400">{t('metabase.fields.description') || 'Description'}</span>
                <span className="font-medium text-gray-900 dark:text-white text-right max-w-xs truncate">{config.description}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">
            {t('metabase.review.configuration') || 'Configuration'}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Database</span>
              <span className="font-medium text-gray-900 dark:text-white">PostgreSQL (application DB)</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Access</span>
              <span className="font-medium text-gray-900 dark:text-white">HTTPS via ALB</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('metabase.review.adminEmail') || 'Admin Email'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{config.adminEmail}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('metabase.review.adminPassword') || 'Password'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{t('metabase.review.adminPasswordValue') || 'Auto-generated (Secrets Manager)'}</span>
            </div>
          </div>
        </div>

        {/* Cost */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900 dark:text-white">
              Costo mensual
            </h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {totalStr}<span className="text-sm font-normal text-gray-500 dark:text-gray-400">/mes</span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render Step ───────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      default: return null;
    }
  };

  // ─── Main Render ───────────────────────────────────────
  return (
    <div className="max-w-[900px] mx-auto px-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/marketplace?product=metabase')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftOutlined />
          <span className="text-sm font-medium">{t('metabase.back') || 'Back to Metabase Services'}</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('metabase.create.title') || 'Create Metabase BI Service'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('metabase.create.subtitle') || 'Deploy a managed Metabase BI instance on dedicated infrastructure with HTTPS access.'}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: t('metabase.steps.infrastructure') || 'Infrastructure' },
            { num: 2, label: t('metabase.steps.metabaseConfig') || 'Metabase Config' },
            { num: 3, label: t('metabase.steps.review') || 'Review' },
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${step >= s.num
                      ? 'bg-black dark:bg-white text-white dark:text-black'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                >
                  {step > s.num ? <CheckCircleOutlined /> : s.num}
                </div>
                <span
                  className={`text-xs mt-2 font-medium ${step >= s.num ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                    }`}
                >
                  {s.label}
                </span>
              </div>
              {idx < 2 && (
                <div
                  className={`h-1 flex-1 mx-2 rounded transition-all ${step > s.num ? 'bg-black dark:bg-white' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {renderStep()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('metabase.actions.back') || 'Back'}
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/marketplace?product=metabase')}
            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('metabase.actions.cancel') || 'Cancel'}
          </button>
          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('metabase.actions.next') || 'Next'}
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <LoadingOutlined className="animate-spin" />
                  {t('metabase.actions.creating') || 'Creating order...'}
                </>
              ) : (
                t('metabase.actions.create') || 'Create Order'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateMetabase;
